import { type Column, Workbook } from 'exceljs';
import { isNil } from 'lodash-es';
import type { CustomColumn } from '@/common/utils/types';
import { roundToDigitNumber } from '@/common/utils/commonUtils';
import type { CommonTreeGridRow } from '@/database/utils/types';
import { PA_TOGGLE_DATA_TYPE } from '@/common/define/common.define';
import { exportExcelFile, formatRowData, getLevelSymbol } from './excel.utils';
import type { ExportExcelOption } from './excel.types';
import { BASE_GRID_DEPTH_COLUMN, GRID_COLUMN_DEFAULT_STYLE } from './excel.define';

const isObjectRow = (rows: any[]) => {
  if (rows.length === 0) {
    return false;
  }

  return rows.every((row) => !(row instanceof Array));
};

const isTreeGridObject = (rows: any[], treeGridSubRowKey: string = 'children') => {
  if (rows.length === 0) {
    return false;
  }

  return rows.some((row) => row[treeGridSubRowKey] instanceof Array);
};

const isColumnHidden = (gridColumn: CustomColumn, option: ExportExcelOption) => {
  return (
    (gridColumn.hide || option.hiddenField?.includes(gridColumn.field)) &&
    !option.shownField?.includes(gridColumn.field)
  );
};

const formatGridColumnsToExcelColumns = (
  gridColumns: Readonly<CustomColumn[]>,
  option: ExportExcelOption,
) => {
  const convertCaptionObject = (option.convertCaption || []).reduce<Record<string, string>>(
    (acc, cur) => {
      acc[cur.field] = cur.caption;
      return acc;
    },
    {},
  );
  return gridColumns.reduce<Partial<Column>[]>((acc, cur) => {
    if (!isColumnHidden(cur, option)) {
      const optionWidth = option.fieldWidth?.[cur.field] ?? (cur.width ?? 400) / 6;

      acc.push({
        header: (cur.attrs?.excelCaption || convertCaptionObject[cur.field]) ?? cur.caption,
        key: cur.field,
        width: optionWidth,
        style: GRID_COLUMN_DEFAULT_STYLE,
      });
    }

    return acc;
  }, []);
};

const formatGridDataToExcelData = (
  gridColumns: Readonly<CustomColumn[]>,
  gridRows: any[], // flatten already
  option: ExportExcelOption,
) => {
  if (gridRows.length === 0) {
    // Empty Array
    return [];
  }
  const { rowDataFormatter, summaryType } = option;
  const isSummaryTypeAvg = summaryType === PA_TOGGLE_DATA_TYPE.AVG;
  const isObject = isObjectRow(gridRows);
  if (isObject) {
    // BaseTreeGrid
    return gridRows.reduce<Record<string, string | number>[]>(
      (acc: Record<string, string | number>[], cur: Record<string, any>, curIndex: number) => {
        const row = {};
        gridColumns.forEach((column, index) => {
          const originalField = column.field;
          const toggledField =
            isSummaryTypeAvg && column.attrs?.toggle ? `${originalField}Avg` : originalField;
          if (!originalField || isColumnHidden(column, option)) {
            return;
          }
          if (rowDataFormatter?.[originalField]) {
            row[originalField] = rowDataFormatter[originalField](cur[toggledField], cur, curIndex);
          } else if (column.rendererType === 'ratio') {
            row[originalField] = isNil(column.decimal)
              ? roundToDigitNumber(cur[toggledField])
              : roundToDigitNumber(cur[toggledField], column.decimal);
          } else {
            row[originalField] = formatRowData(cur[toggledField], gridColumns[index]);
          }
        });
        acc.push(row);
        return acc;
      },
      [],
    );
  }

  // BaseGrid
  const columnsMap = new Map(gridColumns.map((col, idx) => [idx, col]));

  return gridRows.reduce<Record<string, string>[]>((acc, cur, curIndex) => {
    const row = {};
    cur.forEach((rowData: any, index: number) => {
      const column = columnsMap.get(index);
      if (!column || isColumnHidden(column, option)) {
        return;
      }
      const { field } = column;
      if (rowDataFormatter?.[field]) {
        row[field] = rowDataFormatter[field](rowData, cur, curIndex);
      } else if (column.rendererType === 'ratio') {
        row[field] = isNil(column.decimal)
          ? roundToDigitNumber(rowData)
          : roundToDigitNumber(rowData, column.decimal);
      } else {
        row[field] = formatRowData(rowData, column);
      }
    });
    acc.push(row);
    return acc;
  }, []);
};

const getColumns = (gridColumns: Readonly<CustomColumn[]>, isTreeGrid: boolean): CustomColumn[] => {
  return isTreeGrid ? [BASE_GRID_DEPTH_COLUMN, ...gridColumns] : [...gridColumns];
};

const recurFlattenTreeGridData = (
  gridRow: CommonTreeGridRow,
  currentLevel: number,
  isSiblingWithChildrenAppearedBefore: boolean, // 이전에 등장한 형제중 자식있는 형제 등장 여부
): [(CommonTreeGridRow & { level: string })[], boolean] => {
  let isAfterSiblingWithChildren: boolean = false;
  const hasChildren = !!gridRow.children?.length;
  const symbol = getLevelSymbol(currentLevel, hasChildren, isSiblingWithChildrenAppearedBefore);

  const flattenChildren = (gridRow.children ?? []).flatMap((row: CommonTreeGridRow) => {
    const [newChild, newChildHasChildren] = recurFlattenTreeGridData(
      row,
      currentLevel + 1,
      isAfterSiblingWithChildren,
    );
    if (newChildHasChildren) {
      isAfterSiblingWithChildren = true;
    }

    return newChild;
  });

  return [[{ ...gridRow, level: symbol }, ...flattenChildren], hasChildren];
};

const createExcelFile = (
  gridColumns: Readonly<CustomColumn[]>,
  gridRows: CommonTreeGridRow[],
  option: ExportExcelOption,
) => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet();
  const isTreeGrid = isTreeGridObject(gridRows, option.treeGridSubRowKey);

  const columns = getColumns(gridColumns, isTreeGrid);
  const rows = isTreeGrid
    ? gridRows.flatMap((row) => recurFlattenTreeGridData(row, 0, false)[0])
    : gridRows;

  const data = formatGridDataToExcelData(columns, rows, option);
  worksheet.columns = formatGridColumnsToExcelColumns(columns, option);
  worksheet.insertRows(2, data);
  return workbook;
};

export const exportToExcelFromClient = async (
  gridColumns: Readonly<CustomColumn[]>,
  gridRows: CommonTreeGridRow[],
  option: ExportExcelOption = {},
) => {
  const workbook = createExcelFile(gridColumns, gridRows, option);
  await exportExcelFile(workbook, option);
};
